import React from 'react';
import { Star } from 'lucide-react';
import type { LocalQuestion } from '../types';

interface FormPreviewProps {
  formName: string;
  formDescription: string;
  collectName: boolean;
  collectEmail: boolean;
  questions: LocalQuestion[];
}

const PreviewQuestion: React.FC<{ question: LocalQuestion; index: number }> = ({ question, index }) => (
  <div className="admin-feedback-preview-question">
    <div className="admin-feedback-preview-question-label">
      {index + 1}. {question.text} {question.required && <span className="admin-feedback-required">*</span>}
    </div>

    {question.type === 'short_text' && (
      <input type="text" className="admin-feedback-preview-text-input" placeholder="Short answer" readOnly />
    )}

    {question.type === 'paragraph' && (
      <textarea className="admin-feedback-preview-textarea" placeholder="Long answer" rows={3} readOnly />
    )}

    {question.type === 'star_rating' && (
      <div className="admin-feedback-preview-stars">
        {[1, 2, 3, 4, 5].map(star => (
          <Star key={star} size={24} className="admin-feedback-star-icon" />
        ))}
      </div>
    )}

    {question.type === 'multiple_choice_single' && (
      <div className="admin-feedback-preview-options">
        {(question.options || ['Option 1', 'Option 2']).map((opt, i) => (
          <label key={i} className="admin-feedback-preview-option">
            <input type="radio" name={`preview-${question.id}`} disabled />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    )}

    {question.type === 'multiple_choice_multi' && (
      <div className="admin-feedback-preview-options">
        {(question.options || ['Option 1', 'Option 2']).map((opt, i) => (
          <label key={i} className="admin-feedback-preview-option">
            <input type="checkbox" disabled />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    )}

    {question.type === 'slider' && (
      <div className="admin-feedback-preview-slider-container">
        <input
          type="range"
          min={question.sliderMin || 1}
          max={question.sliderMax || 10}
          defaultValue={(question.sliderMin || 1) + Math.floor(((question.sliderMax || 10) - (question.sliderMin || 1)) / 2)}
          className="admin-feedback-preview-slider"
          disabled
        />
        <div className="admin-feedback-preview-slider-labels">
          <span>{question.sliderMin || 1}</span>
          <span>{question.sliderMax || 10}</span>
        </div>
      </div>
    )}
  </div>
);

const MemoizedPreviewQuestion = React.memo(PreviewQuestion);

const FormPreview: React.FC<FormPreviewProps> = ({
  formName,
  formDescription,
  collectName,
  collectEmail,
  questions,
}) => {
  return (
    <div className="admin-feedback-builder-preview">
      <div className="admin-feedback-preview-header">
        <h4>Preview</h4>
      </div>
      <div className="admin-feedback-preview-content">
        {formName && <h3 className="admin-feedback-preview-title">{formName}</h3>}
        {formDescription && <p className="admin-feedback-preview-desc">{formDescription}</p>}

        {collectName && (
          <div className="admin-feedback-preview-question">
            <div className="admin-feedback-preview-question-label">Your Name *</div>
            <input type="text" className="admin-feedback-preview-text-input" placeholder="Enter your name" readOnly />
          </div>
        )}

        {collectEmail && (
          <div className="admin-feedback-preview-question">
            <div className="admin-feedback-preview-question-label">Your Email *</div>
            <input type="email" className="admin-feedback-preview-text-input" placeholder="Enter your email" readOnly />
          </div>
        )}

        {questions.map((question, index) => (
          <MemoizedPreviewQuestion key={question.id} question={question} index={index} />
        ))}

        {questions.length === 0 && !collectName && !collectEmail && (
          <div className="admin-feedback-preview-empty">
            Add questions to see preview
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(FormPreview);
